import React, { useMemo, useState } from 'react';
import { Button, Textarea } from '@headlessui/react';
import { Form, Formik, FormikProps } from 'formik';
import { ArrowUp, Briefcase, Building2, User } from 'lucide-react';
import toast from 'react-hot-toast';
import Breadcrumbs from 'components/common/Breadcrumbs';
import CustomCalendar from 'components/common/CustomCalendar';
import { CustomSelect } from 'components/common/CustomSelect';
import FormInput from 'components/common/FormInput';
import { LoaderButton } from 'components/common/LoadingButton';
import WarningDialog from 'components/common/WarningDialog';
import OwnerSearchDropdown from 'components/modules/CRM/Dropdowns/OwnerSearchDropdown';
import SearchCreateSelect, { SearchCreateOption } from 'components/common/SearchCreateSelect';
import SuggestedMember from 'components/common/SuggestedMember';
import SuggestedUserListShimmer from 'components/Loaders/SuggestedUserListShimmer';
import UserAvatar from 'components/common/UserAvatar';
import { useCrmNavigation } from 'hooks/useCrmNavigation';
import {
    useCompaniesInfinite,
    useContactOwners,
    useContactsQuery,
    useDealsQuery,
} from 'service/crm';
import { useCreateCrmTaskMutation } from 'service/crm/hooks/tasks/useCrmTasks';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import { Contact, ContactOwner } from 'types/crm/contacts';
import { Company } from 'types/crm/company';
import { Deal } from 'types/crm/deal';
import { priorityOptions, TaskFormValues, taskStatusOptions, taskTypeOptions } from 'types/crm/task';
import { formatDate, formatDateToYmd } from 'utils/date';
import { taskFormInitialValues, taskFormValidationSchema } from 'utils/validation';
import RequiredMark from 'components/common/RequiredMark';


const TASK_PRIORITY_TEXT: Record<string, string> = {
    LOW: 'text-green-500',
    MEDIUM: 'text-yellow-500',
    HIGH: 'text-red-500',
};

export type RelatedToType = 'CONTACT' | 'COMPANY' | 'DEAL';

const RELATED_TABS: { key: RelatedToType; label: string; icon: React.ReactNode }[] = [
    { key: 'CONTACT', label: 'Contact', icon: <User size={16} /> },
    { key: 'COMPANY', label: 'Company', icon: <Building2 size={16} /> },
    { key: 'DEAL', label: 'Deal', icon: <Briefcase size={16} /> },
];

export interface TaskPresetTarget {
    targetType: RelatedToType;
    targetReference: string;
    label: string;
    subtitle?: string;
}

interface CreateTaskProps {
    presetTarget?: TaskPresetTarget;
    onCreated?: () => void;
}

const CreateTaskPage: React.FC<CreateTaskProps> = ({ presetTarget, onCreated }) => {
    const authUser = useAppSelector(selectAuthUser);
    const { buildTasksUrl, buildCreateTaskUrl, toTasks } = useCrmNavigation();
    const [showCancelWarning, setShowCancelWarning] = useState(false);

    const createTaskMutation = useCreateCrmTaskMutation();
    const isSaving = createTaskMutation.isPending;

    const { data: owners = [], isLoading: ownersLoading } = useContactOwners(
        authUser?.orgUuid as string,
    );
    const ownerOptions = useMemo(
        () => owners.map((o: ContactOwner) => ({ value: o.uuid, label: o.name.trim() })),
        [owners],
    );

    // --- Related to: which record this task is linked to ----------------------
    const [relatedTab, setRelatedTab] = useState<RelatedToType>(presetTarget?.targetType ?? 'CONTACT');
    const [relatedTo, setRelatedTo] = useState<SearchCreateOption | null>(
        presetTarget
            ? { uuid: presetTarget.targetReference, name: presetTarget.label, subtitle: presetTarget.subtitle, imageUrl: null }
            : null,
    );
    const [relatedSearch, setRelatedSearch] = useState('');
    const isTargetLocked = !!presetTarget;

    const {
        contacts,
        isLoading: contactsSearchLoading,
        isFetchingMore: contactsFetchingMore,
        hasMore: contactsHasMore,
        onLoadMore: loadMoreContacts,
    } = useContactsQuery({ search: relatedSearch || undefined, size: 20 });

    const {
        companies: companyResults,
        isLoading: companiesSearchLoading,
        isFetchingMore: companiesFetchingMore,
        hasMore: companiesHasMore,
        onLoadMore: loadMoreCompanies,
    } = useCompaniesInfinite(authUser?.orgUuid as string, {
        search: relatedSearch || undefined,
        size: 20,
    });

    const {
        deals: dealResults,
        isLoading: dealsSearchLoading,
        isFetchingMore: dealsFetchingMore,
        hasMore: dealsHasMore,
        onLoadMore: loadMoreDeals,
    } = useDealsQuery({
        search: relatedSearch || undefined,
        size: 20,
    });

    const contactOptions = useMemo<SearchCreateOption[]>(
        () =>
            (contacts ?? []).map((c: Contact) => ({
                uuid: c.uuid,
                name: c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' '),
                subtitle: [c.jobTitle, c.companyName].filter(Boolean).join(' · ') || c.email,
                imageUrl: c.profileImageUrl || null,
            })),
        [contacts],
    );

    const companyOptions = useMemo<SearchCreateOption[]>(
        () =>
            companyResults.map((c: Company) => ({
                uuid: c.uuid,
                name: c.name,
                imageUrl: c.imageUrl || null,
                subtitle: [c.industry, c.cityLocation].filter(Boolean).join(' · '),
            })),
        [companyResults],
    );

    const dealOptions = useMemo<SearchCreateOption[]>(
        () =>
            (dealResults ?? []).map((d: Deal) => ({
                uuid: d.uuid,
                name: d.name,
                imageUrl: null,
                subtitle: d.dealStage,
            })),
        [dealResults],
    );

    const activeRelatedOptions =
        relatedTab === 'CONTACT' ? contactOptions : relatedTab === 'COMPANY' ? companyOptions : dealOptions;
    const activeRelatedLoading =
        relatedTab === 'CONTACT' ? contactsSearchLoading : relatedTab === 'COMPANY' ? companiesSearchLoading : dealsSearchLoading;
    const activeRelatedFetchingMore =
        relatedTab === 'CONTACT' ? contactsFetchingMore : relatedTab === 'COMPANY' ? companiesFetchingMore : dealsFetchingMore;
    const activeRelatedHasMore =
        relatedTab === 'CONTACT' ? contactsHasMore : relatedTab === 'COMPANY' ? companiesHasMore : dealsHasMore;
    const activeRelatedLoadMore =
        relatedTab === 'CONTACT' ? loadMoreContacts : relatedTab === 'COMPANY' ? loadMoreCompanies : loadMoreDeals;

    // const handleTabChange = (tab: RelatedToType) => {
    //     setRelatedTab(tab);
    //     setRelatedSearch('');
    // };
    const handleTabChange = (tab: RelatedToType) => {
        // Once a record is selected, the entity type is locked — remove it first to switch.
        if (relatedTo) return;
        setRelatedTab(tab);
        setRelatedSearch('');
    };

    const handleSelectRelated = (option: SearchCreateOption) => {
        setRelatedTo(option);
        setRelatedSearch('');
    };

    // Required fields shown in the checklist / gating the submit button.
    // "Related to" only counts as a field when the caller hasn't already fixed it.
    const baseRequiredFields: { key: keyof TaskFormValues; label: string }[] = [
        { key: 'title', label: 'Title' },
        { key: 'taskType', label: 'Task type' },
        { key: 'priority', label: 'Priority' },
        { key: 'taskStatus', label: 'Status' },
        // { key: 'taskOwnerUuid', label: 'Assignee' }, 
        // { key: 'dueAt', label: 'Due date' },
    ];

    const handleSubmit = async (values: TaskFormValues) => {
        if (!relatedTo) {
            toast.error('Select a contact, company, or deal to link this task to.');
            return;
        }

        const dueAtIso = values.dueAt ? new Date(values.dueAt).toISOString() : undefined;

        try {
            await createTaskMutation.mutateAsync({
                targetType: relatedTab,
                targetReference: relatedTo.uuid,
                title: values.title,
                description: values.description || undefined,
                dueAt: dueAtIso,
                taskType: values.taskType,
                priority: values.priority,
                taskStatus: values.taskStatus,
                taskOwnerUuid: values.taskOwnerUuid || undefined,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            toast.success('Task created successfully');
            if (onCreated) {
                onCreated();
            } else {
                toTasks();
            }
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ?? err?.message ?? 'Failed to create task. Please try again.',
            );
        }
    };
    return (
        <div >
            {!presetTarget && (
                <div className="shrink-0 border-b border-primary-subtle mb-4 pb-4">
                    <Breadcrumbs
                        items={[
                            { label: 'Tasks', path: buildTasksUrl() },
                            { label: 'Create new task', path: buildCreateTaskUrl() },
                        ]}
                        className="mb-4"
                        activeLinkProps={{ className: 'text-neutral font-semibold' }}
                    />
                    <div>

                        <p className="font-medium text-sm text-neutral-faded">Add a new task and assign it to keep work moving.</p>
                    </div>
                </div>
            )}

            <Formik<TaskFormValues>
                initialValues={taskFormInitialValues}
                validationSchema={taskFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    dirty,
                    handleChange,
                    handleBlur,
                    resetForm,
                    setFieldValue,
                }: FormikProps<TaskFormValues>) => {
                    const requiredFields = isTargetLocked
                        ? baseRequiredFields
                        : [...baseRequiredFields, { key: 'relatedTo' as keyof TaskFormValues, label: 'Related to' }];

                    const isFieldDone = (key: keyof TaskFormValues) =>
                        key === 'relatedTo' ? !!relatedTo : !!values[key];

                    const completedRequired = requiredFields.filter(({ key }) => isFieldDone(key));

                    const selectedOwnerLabel = ownerOptions.find((o) => o.value === values.taskOwnerUuid)?.label;
                    const selectedStatusLabel = taskStatusOptions.find((o: any) => o.value === values.taskStatus)?.label;
                    const selectedPriorityLabel = priorityOptions.find((o: any) => o.value === values.priority)?.label;
                    const priorityColor = values.priority
                        ? TASK_PRIORITY_TEXT[values.priority as string] ?? 'text-neutral-medium'
                        : 'text-neutral-medium';
                    const previewDueDate = values.dueAt ? formatDate(values.dueAt) : null;

                    const handleCancel = () => {
                        if (!dirty && (isTargetLocked || !relatedTo)) {
                            if (onCreated) onCreated();
                            else toTasks();
                            return;
                        }
                        setShowCancelWarning(true);
                    };

                    return (
                        <Form className="flex flex-col flex-1 min-h-0">
                            <div className="flex-1 min-h-0 overflow-y-auto pr-2 -mr-2 mt-4">
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                                    {/* Left column: live preview + required-field checklist */}
                                    <div className="lg:col-span-1 flex flex-col gap-4 lg:sticky lg:top-0">

                                        <div className="rounded-lg border border-primary-subtle overflow-hidden">
                                            <div className="h-[3px] bg-gradient-to-r from-[#6366f1] to-[#8b5cf6]" />
                                            <div className="p-5">
                                                <p className="text-xs font-semibold text-neutral-medium uppercase tracking-wide mb-4">
                                                    Preview
                                                </p>

                                                <div>
                                                    {selectedStatusLabel ? (
                                                        <span className="inline-block rounded-md bg-primary-light px-2 py-1 text-xs font-medium text-primary-emphasis">
                                                            {selectedStatusLabel}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-block px-3 py-1 rounded-md text-xs font-medium text-color-accent bg-primary-subtle">
                                                            No status
                                                        </span>
                                                    )}

                                                    <h4 className="mt-3 font-semibold text-neutral-faded break-words">
                                                        {values.title || 'Untitled task'}
                                                    </h4>

                                                    {values.description ? (
                                                        <p className="mt-1 text-xs text-neutral-medium line-clamp-3">
                                                            {values.description}
                                                        </p>
                                                    ) : null}

                                                    <div className="mt-4 border-t border-primary-subtle pt-4">
                                                        <p className="text-xs text-neutral-medium">
                                                            {previewDueDate ? `Closes ${previewDueDate}` : 'No due date'}
                                                        </p>
                                                    </div>

                                                    <div className="mt-4 border-t border-primary-subtle pt-4 flex items-center gap-2">
                                                        <ArrowUp className={`w-3.5 h-3.5 shrink-0 ${priorityColor}`} />
                                                        <span className={`text-sm font-medium ${priorityColor}`}>
                                                            {selectedPriorityLabel || 'No priority'}
                                                        </span>
                                                    </div>

                                                    <div className="mt-4 border-t border-primary-subtle pt-4 flex items-center gap-2">
                                                        {selectedOwnerLabel ? (
                                                            <UserAvatar name={selectedOwnerLabel} className="w-6 h-6 text-[10px]" />
                                                        ) : (
                                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-light">
                                                                <User size={14} />
                                                            </span>
                                                        )}
                                                        <span
                                                            className={`truncate text-sm ${selectedOwnerLabel ? 'text-neutral' : 'text-neutral-medium'
                                                                }`}
                                                        >
                                                            {selectedOwnerLabel || 'Unassigned'}
                                                        </span>
                                                    </div>

                                                    {relatedTo && (
                                                        <div className="mt-4 border-t border-primary-subtle pt-4">
                                                            <p className="text-xs text-neutral-medium">
                                                                Linked to{' '}
                                                                <span className="text-neutral font-medium">{relatedTo.name}</span>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Required-fields checklist — hidden for now, kept for reference.
                                        <div className="rounded-lg border border-primary-subtle p-5">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-medium text-neutral">Required fields</p>
                                                <span className="text-xs text-neutral-medium">
                                                    {completedRequired.length} / {requiredFields.length}
                                                </span>
                                            </div>
                                            <ul className="mt-4 flex flex-col gap-3">
                                                {requiredFields.map(({ key, label }) => {
                                                    const isDone = isFieldDone(key);
                                                    return (
                                                        <li key={key as string} className="flex items-center gap-2 text-sm">
                                                            {isDone ? (
                                                                <CheckCircleIcon size={16} />
                                                            ) : (
                                                                <span className="flex h-4 w-4 shrink-0 rounded-full border border-primary-subtle" />
                                                            )}
                                                            <span className={isDone ? 'text-neutral' : 'text-neutral-medium'}>
                                                                {label}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                        */}
                                    </div>

                                    {/* Right column: the form itself */}
                                    <div className="lg:col-span-3 flex flex-col gap-5">
                                        <div className="border border-primary-subtle rounded-lg p-5">
                                            <h3 className="font-semibold text-sm text-neutral-medium tracking-wide">
                                                Task Details
                                            </h3>
                                            <div className="mt-6 flex flex-col gap-5">
                                                <div className="grid grid-cols-1 gap-5">
                                                    <FormInput
                                                        label="Title"
                                                        name="title"
                                                        value={values.title}
                                                        placeholder="e.g. Send updated proposal"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.title && errors.title}
                                                        darkBg={true}
                                                        whiteLabel={false}
                                                        required
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 gap-3">
                                                    <label className="text-sm text-neutral-faded block">Description</label>
                                                    <div className="rounded-lg border border-primary-subtle bg-primary-light overflow-hidden w-full transition-colors">
                                                        <Textarea
                                                            name="description"
                                                            value={values.description}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="Add more detail"
                                                            rows={3}
                                                            className="block w-full h-full bg-transparent p-3 text-sm text-neutral placeholder:font-normal placeholder:text-base placeholder:text-[#374151] focus:outline-none resize-none transition-colors"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                    <CustomSelect
                                                        label="Task Type"
                                                        name="taskType"
                                                        options={taskTypeOptions}
                                                        value={values.taskType}
                                                        onChange={(value) => setFieldValue('taskType', value)}
                                                        onBlur={handleBlur}
                                                        placeholder="Select Task Type"
                                                        error={touched.taskType && errors.taskType}
                                                        whiteLabel={false}
                                                        darkBg
                                                        required
                                                    />
                                                    <CustomSelect
                                                        label="Task Status"
                                                        name="taskStatus"
                                                        options={taskStatusOptions}
                                                        value={values.taskStatus}
                                                        onChange={(value) => setFieldValue('taskStatus', value)}
                                                        onBlur={handleBlur}
                                                        placeholder="Select Task Status"
                                                        error={touched.taskStatus && errors.taskStatus}
                                                        whiteLabel={false}
                                                        darkBg
                                                        required
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                    <CustomSelect
                                                        label="Priority"
                                                        name="priority"
                                                        options={priorityOptions}
                                                        value={values.priority}
                                                        onChange={(value) => setFieldValue('priority', value)}
                                                        onBlur={handleBlur}
                                                        placeholder="Select Priority"
                                                        error={touched.priority && errors.priority}
                                                        whiteLabel={false}
                                                        darkBg
                                                        valueClassName={priorityColor}
                                                        icon={<ArrowUp className={`w-3.5 h-3.5 ${priorityColor}`} />}
                                                        required
                                                    />
                                                    <CustomCalendar
                                                        label="Due Date"
                                                        placeholder="mm/dd/yyyy"
                                                        value={values.dueAt ? new Date(values.dueAt) : null}
                                                        className="bg-primary-light placeholder:text-primary-intemediate"
                                                        onChange={(date) => setFieldValue('dueAt', formatDateToYmd(date))}
                                                        minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                                    <div>
                                                        <label className="text-sm text-neutral-faded block mb-2">Assign To</label>
                                                        <OwnerSearchDropdown
                                                            owners={owners}
                                                            currentOwnerUuid={values.taskOwnerUuid}
                                                            onOwnerChange={(owner) => setFieldValue('taskOwnerUuid', owner?.uuid ?? null)}
                                                            placeholder={ownersLoading ? 'Loading owners...' : 'Select Assignee'}
                                                            allowUnassigned={true}
                                                            unassignedLabel="None"
                                                            size="md"

                                                            className="w-full bg-primary-light border border-primary-subtle rounded-lg"
                                                        />
                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                        {/* Related to */}
                                        <div className="border border-primary-subtle rounded-lg p-5">
                                            <h3 className="font-semibold text-sm text-neutral-faded tracking-wide">Related to <RequiredMark /></h3>

                                            {isTargetLocked ? (
                                                <div className="mt-4 flex items-center gap-2 rounded-lg border border-primary-subtle bg-primary-intemediate/40 px-3 py-2">
                                                    <UserAvatar name={presetTarget!.label} className="w-7 h-7 text-[11px]" />
                                                    <div>
                                                        <p className="text-sm font-medium text-neutral">{presetTarget!.label}</p>
                                                        {presetTarget!.subtitle ? (
                                                            <p className="text-xs text-neutral-medium">{presetTarget!.subtitle}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="mt-1 text-xs text-neutral-faded">
                                                        Associate this task with a Contact, Company, or Deal to track work in the right context.
                                                    </p>

                                                    {/* <div className="mt-5 flex items-center gap-6 border-b border-primary-subtle">
                                                        {RELATED_TABS.map((tab) => (
                                                            <button
                                                                key={tab.key}
                                                                type="button"
                                                                onClick={() => handleTabChange(tab.key)}
                                                                className={`flex items-center gap-2 pb-3 text-sm font-medium  -mb-px transition-colors ${relatedTab === tab.key
                                                                    ? ' text-neutral'
                                                                    : ' text-neutral-medium hover:text-neutral'
                                                                    }`}
                                                            >
                                                                {tab.icon}
                                                                {tab.label}
                                                            </button>
                                                        ))}
                                                    </div> */}

                                                    <div className="mt-5 flex items-center gap-6 border-b border-primary-subtle">
                                                        {RELATED_TABS.map((tab) => {
                                                            const isActive = relatedTab === tab.key;
                                                            const isDisabled = !!relatedTo && !isActive;
                                                            return (
                                                                <button
                                                                    key={tab.key}
                                                                    type="button"
                                                                    onClick={() => handleTabChange(tab.key)}
                                                                    disabled={isDisabled}
                                                                    className={`flex items-center gap-2 pb-3 text-sm font-medium -mb-px transition-colors ${isActive
                                                                        ? 'text-neutral'
                                                                        : isDisabled
                                                                            ? 'text-neutral-faded'
                                                                            : 'text-neutral-medium hover:text-neutral'
                                                                        }`}
                                                                >
                                                                    {tab.icon}
                                                                    {tab.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <div className="mt-4">
                                                        {relatedTo ? (
                                                            <div className="mb-3 flex items-center justify-between rounded-lg border border-primary-subtle bg-primary-intemediate/40 px-3 py-2">
                                                                <div className="flex items-center gap-2">
                                                                    <UserAvatar name={relatedTo.name} className="w-7 h-7 text-[11px]" />
                                                                    <div>
                                                                        <p className="text-sm font-medium text-neutral">{relatedTo.name}</p>
                                                                        {relatedTo.subtitle ? (
                                                                            <p className="text-xs text-neutral-medium">{relatedTo.subtitle}</p>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setRelatedTo(null)}
                                                                    className="text-xs text-neutral-medium hover:text-neutral"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <SearchCreateSelect
                                                                placeholder={`Search ${relatedTab.toLowerCase()}s...`}
                                                                listHeading={RELATED_TABS.find((t) => t.key === relatedTab)?.label}
                                                                emptyLabel={`No ${relatedTab.toLowerCase()}s found`}
                                                                icon={relatedTab === 'COMPANY' ? Building2 : relatedTab === 'DEAL' ? Briefcase : User}
                                                                hideCreateButton
                                                                showOptionAddAction
                                                                clearOnSelect
                                                                value={relatedSearch}
                                                                options={activeRelatedOptions}
                                                                isLoading={activeRelatedLoading}
                                                                isFetchingMore={activeRelatedFetchingMore}
                                                                hasMore={activeRelatedHasMore}
                                                                onLoadMore={activeRelatedLoadMore}
                                                                loadingContent={<SuggestedUserListShimmer />}
                                                                renderOption={(option, select) => (
                                                                    <SuggestedMember
                                                                        item={{
                                                                            id: option.uuid,
                                                                            name: option.name,
                                                                            subtitle: option.subtitle,
                                                                            imageUrl: option.imageUrl,
                                                                        }}
                                                                        isSelected={false}
                                                                        onSelect={select}
                                                                        leading={
                                                                            relatedTab !== 'CONTACT' ? (
                                                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-variant">
                                                                                    {relatedTab === 'COMPANY' ? (
                                                                                        <Building2 size={15} className="text-neutral-medium" />
                                                                                    ) : (
                                                                                        <Briefcase size={15} className="text-neutral-medium" />
                                                                                    )}
                                                                                </span>
                                                                            ) : undefined
                                                                        }
                                                                    />
                                                                )}
                                                                onSearchChange={setRelatedSearch}
                                                                onSelectOption={handleSelectRelated}
                                                                dropdownPosition="static"
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="shrink-0 mt-3 pt-3 border-t border-primary-subtle flex flex-wrap items-center justify-between gap-4">
                                <p className="text-sm text-neutral-medium">
                                    Fields marked <span className="text-neutral-medium">*</span> are required
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        type="button"
                                        className="py-3 px-5 rounded-lg border text-sm border-primary-subtle"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </Button>
                                    <LoaderButton
                                        className="!mt-0"
                                        text="Create Task"
                                        isLoading={isSaving}
                                        disabled={completedRequired.length !== requiredFields.length || isSaving}
                                    />
                                </div>
                            </div>

                            <WarningDialog
                                open={showCancelWarning}
                                title="Discard unsaved changes?"
                                description="You have unsaved changes. Are you sure you want to leave without saving?"
                                confirmText="Discard"
                                cancelText="Keep editing"
                                variant="danger"
                                onConfirm={() => {
                                    setShowCancelWarning(false);
                                    resetForm();
                                    if (!isTargetLocked) setRelatedTo(null);
                                    if (onCreated) onCreated();
                                    else toTasks();
                                }}
                                onCancel={() => setShowCancelWarning(false)}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CreateTaskPage;